// schedulePresentationModal.jsx
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import DynamicFormInput from "components/form/form";
import React, { useEffect, useState } from "react";
import { updateLegalResearchRequest } from "services/legalResearchService";
import { LoadingIcon } from "utils/constant";
import { LEGAL_RESEARCH_REQUEST_STATUS, LEGISLATIVE_DRAFTING_REQUEST_STATUS } from "utils/constantValues";
import { createResearchSchedule } from "services/researchScheduleService";
import { updateLegislativeDrafting } from "services/legislativeService";
import { LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { useTranslation } from "react-i18next";

interface SchedulePresentationModalProps {
    data: any;
    setLegalResearch: Function;
    isOpen: boolean;
    onClose: () => void;
}

const UploadAmendedFile: React.FC<SchedulePresentationModalProps> = ({
    data: singleData,
    setLegalResearch,
    isOpen,
    onClose,
}) => {
    const { t } = useTranslation();
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useToast();
    const [employees, setEmployees] = useState([]);
    const contextData = useAuth();

    const [formState, setFormState] = useState({
        amended_law_file: null as File | null,
        public_feedback: null as File | null,
    });

    const handleFileChange = (
        file: any,
        fieldName: keyof Pick<
            typeof formState,
            "amended_law_file"
        >
    ) => {
        setFormState((prev) => ({
            ...prev,
            [fieldName]: file,
        }));
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    if (!isOpen) return null;

    const handleAssignSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);

        const data = new FormData();

        data.append("amended_law_file", formState?.amended_law_file ?? "");

        if (singleData?.uploaded_files?.find((file: any) => file.document_type === "public_feedback"))
            data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.public_and_amended_file_submitted.value);
        if (singleData?.current_status === LEGISLATIVE_DRAFTING_REQUEST_STATUS.audit_responded.value)
            data.append("current_status", LEGISLATIVE_DRAFTING_REQUEST_STATUS.audit_submitted.value);

        try {
            const response = await updateLegislativeDrafting(singleData.id, data);

            setLegalResearch((prev: any) => ({
                ...prev,
                ...response,
            }));

            showToast(t("LegalResearchForwardedSuccess") || "Legal research forwarded successfully!", "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegalResearchRequest") || "Failed to update legal research request.", "error");
        } finally {
            setSubmitting(false);
        }

        onClose(); // Close modal after assignment
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
            <form
                onSubmit={handleAssignSubmit}
                className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6 relative"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                        />
                    </svg>
                </button>

                <h2 className="text-xl font-semibold text-gray-800 mb-6">
                    {t("AmendedFile") || "Amended File"}
                </h2>

                {/* Remark Section */}
                <div className="mb-4">
                    <DynamicFormInput
                        inputs={[
                            {
                                // No 'value' prop for file inputs
                                type: "file",
                                label: t("AmendedLegislativeFile") || "Amended Legislative File",
                                name: "amended_law_file",
                                onChange: (file) => handleFileChange(file, "amended_law_file"),
                            },
                        ]}
                    />
                </div>
                {/* Assign Button */}
                <div className="text-center mt-6 flex gap-4">
                    <button
                        type="submit"
                        disabled={submitting}
                        className="bg-[#212121] hover:bg-green-600 text-white font-bold py-3 px-8 rounded-md w-full disabled:opacity-70"
                    >
                        {submitting ? (
                            <div className="flex items-center justify-center">
                                {LoadingIcon} {t("Submitting") || "Submitting..."}
                            </div>
                        ) : (
                            t("Submit") || "Submit"
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UploadAmendedFile;